import { useMemo } from 'react';
import { getItemWithExpiration } from '../../lib/utils';
import { isOpsBriefingMock } from '../../api/opsBriefing';

/**
 * 브리핑 화면 네 곳이 공통으로 쓰는 인증·센터 정보.
 *
 * 페이지마다 localStorage 를 직접 읽으면 센터 전환(center-view)이나
 * 토큰 만료 처리가 화면마다 갈립니다. 한 곳에서만 읽습니다.
 */
export type OpsContext = {
    accessToken: string;
    centerId: string;
    /**
     * 지금 보고 있는 센터의 이름 — 없으면 빈 문자열.
     *
     * 조회에는 안 쓴다. **사람에게 보여 줄 때**만 쓴다(리포트 머리글, PDF 파일명).
     * 로그인 시 `authObject.centerName` 에 담기는데(SignInPage), 구버전 세션에는
     * 없을 수 있어서 **없어도 되는 값**으로 다룬다 — 없으면 그 자리를 뺀다.
     */
    centerName: string;
    /** 조회에 필요한 값이 다 있는가 */
    ready: boolean;
    /**
     * 이 계정으로 **선물을 보낼 수 있나** (포인트가 나가는 동작).
     *
     * 서버는 role 이 admin 이 아니면 403 입니다(gifticonScope.resolveAdminScope).
     * 그런데 일반 원장 로그인은 authObject 에 role 을 담지 않습니다 —
     * superadmin 과 센터 열람 모드만 표시됩니다. 그래서 "그 둘이 아니면 원장"으로 봅니다.
     * (센터 열람 모드는 api 클라이언트가 쓰기 요청 자체를 막기도 합니다)
     *
     * 여기서 판정하는 이유: 이 값은 **버튼을 그리기 전에** 필요합니다.
     * 선물 화면 쪽에서 읽으면 authObject 를 읽는 곳이 둘로 갈립니다.
     */
    canSendGift: boolean;
};

export const useOpsContext = (): OpsContext => {
    // 로그인 정보는 렌더 중에 바뀌지 않는다. 매 렌더마다 파싱할 이유가 없다.
    const authorization = useMemo(() => getItemWithExpiration('authorization'), []);
    const authObject = useMemo(() => getItemWithExpiration('authObject'), []);

    const accessToken = typeof authorization === 'string' ? authorization : '';
    const centerId = authObject?.centerId ?? '';
    const centerName = typeof authObject?.centerName === 'string' ? authObject.centerName : '';
    const isCenterViewer = authObject?.accessMode === 'view' || authObject?.role === 'center_viewer';
    const canSendGift = !!authObject && !isCenterViewer && authObject?.role !== 'superadmin';

    /*
     * 목 모드에서는 서버를 부르지 않으므로 토큰·센터가 없어도 화면이 돌아야 합니다.
     * (서버 연동 전 화면을 보여드리거나 데모할 때)
     * 실서버 모드에서는 둘 다 있어야만 조회합니다 — 없으면 401 이 쏟아집니다.
     */
    if (isOpsBriefingMock()) {
        /*
         * 목 모드에서도 선물은 보내지 않습니다. 발송만은 실서버로 나가는 동작이라
         * 데모 화면에서 눌러 볼 수 있게 두면 진짜 포인트가 나갑니다.
         */
        return {
            accessToken: accessToken || 'mock',
            centerId: centerId || 'mock-center',
            centerName,
            ready: true,
            canSendGift: false,
        };
    }

    return { accessToken, centerId, centerName, ready: !!accessToken && !!centerId, canSendGift };
};
